/**
 * Audio Manager - Gestione audio principale
 * 
 * @since v3.0.0 - Rimossa logica preroll, gestita da AdManager
 */

export class AudioManager {
  constructor(config, stateManager, adManager = null) {
    this.config = config;
    this.state = stateManager;
    this.adManager = adManager;
    this.controlsManager = null; // Impostato dopo dalla SashaPlayer
    
    this.audioElement = null;
    this.audioContext = null;
    this.analyser = null;
    this.dataArray = null;
    this.gainNode = null; // GainNode per controllo volume iOS-compatible
    
    // Midroll monitoring
    this.lastCheckedTime = 0;
    
    this.setupAudio();
  }
  
  /**
   * Setup elementi audio
   */
  setupAudio() {
    // Audio principale
    this.audioElement = new Audio();
    this.audioElement.preload = 'none'; // Lazy loading per Web Vitals
    
    // Non impostare src immediatamente per ottimizzare LCP
    // Sarà caricato on-demand quando necessario
    
    this.attachAudioListeners();
  }
  
  /**
   * Carica audio lazy quando necessario
   * Ottimizza LCP evitando download immediato
   */
  async loadAudio() {
    if (this.audioElement.src || !this.config.audioUrl) return;
    
    try {
      // Per Spreaker, potrebbe essere necessario crossOrigin
      if (this.config._spreaker) {
        this.audioElement.crossOrigin = 'anonymous';
      }
      
      this.audioElement.src = this.config.audioUrl;
      this.audioElement.preload = 'metadata';
      
      // Aggiungi listener per errori
      this.audioElement.addEventListener('error', (e) => {
        console.error('❌ Audio element error:', {
          error: e,
          code: this.audioElement.error?.code,
          message: this.audioElement.error?.message,
          src: this.audioElement.src
        });
      });
      
      await this.audioElement.load();
      
      console.log('🎵 Audio caricato lazy:', this.config.audioUrl);
      console.log('📊 Audio element state:', {
        src: this.audioElement.src,
        readyState: this.audioElement.readyState,
        networkState: this.audioElement.networkState,
        error: this.audioElement.error
      });
    } catch (error) {
      console.error('Errore caricamento audio:', error);
    }
  }
  
  /**
   * Attach listeners audio
   */
  attachAudioListeners() {
    // Audio principale
    this.audioElement.addEventListener('loadedmetadata', () => {
      const duration = this.audioElement.duration;
      this.state.setState({ duration });
      
      // Calcola midroll points quando si conosce la durata
      if (this.adManager) {
        this.adManager.calculateMidrollPoints(duration);
      }
    });
    
    this.audioElement.addEventListener('timeupdate', async () => {
      const currentTime = this.audioElement.currentTime;
      this.state.setState({ currentTime });
      
      // Controlla midroll triggers (solo se non stiamo riproducendo un ad)
      const { isAdPlaying } = this.state.getState();
      if (!isAdPlaying && this.adManager && Math.floor(currentTime) !== Math.floor(this.lastCheckedTime)) {
        this.lastCheckedTime = currentTime;
        const midrollToPlay = this.adManager.checkMidrollTrigger(currentTime);
        
        if (midrollToPlay) {
          console.log('⏸️ Midroll trigger - pausa contenuto principale');
          this.audioElement.pause();
          
          this.state.setState({ isAdPlaying: true, currentAdType: 'midroll', currentAdId: midrollToPlay.id });
          if (this.controlsManager) this.controlsManager.disableControlsForAd();
          
          // Riproduce midroll
          await this.adManager.playAd(midrollToPlay, this.getHostElement());
          
          // Riprendi contenuto principale
          this.state.setState({ isAdPlaying: false, currentAdType: null, currentAdId: null });
          if (this.controlsManager) this.controlsManager.enableControlsAfterAd();
          console.log('▶️ Midroll completato - riprendo contenuto');
          this.audioElement.play();
        }
      }
    });
    
    this.audioElement.addEventListener('ended', async () => {
      // Riproduci postrolls se presenti
      if (this.adManager) {
        const postrolls = this.adManager.getPostrolls();
        if (postrolls.length > 0) {
          console.log('▶️ Contenuto terminato - inizio postrolls');
          this.state.setState({ isAdPlaying: true, currentAdType: 'postroll' });
          if (this.controlsManager) this.controlsManager.disableControlsForAd();
          
          await this.adManager.playPostrolls(this.getHostElement());
          
          this.state.setState({ isAdPlaying: false, currentAdType: null, currentAdId: null });
          if (this.controlsManager) this.controlsManager.enableControlsAfterAd();
        }
      }
      
      this.state.setState({ isPlaying: false });
    });
    
    this.audioElement.addEventListener('error', (e) => {
      console.error('Errore audio:', e);
      this.state.setState({ isLoading: false });
    });
  }
  
  /**
   * Ottieni host element per eventi
   * @returns {HTMLElement}
   * @private
   */
  getHostElement() {
    // Assume che AudioManager sia usato in SashaPlayer
    // L'host element sarà passato esplicitamente o trovato
    return window.document.querySelector('sasha-player') || window.document;
  }
  
  /**
   * Play audio (con gestione prerolls)
   * 
   * @since v3.0.0 - Integrato con AdManager per prerolls
   */
  async play() {
    try {
      // Carica audio lazy se non ancora caricato
      await this.loadAudio();
      
      // Controlla se ci sono prerolls NON ancora riprodotti
      // IMPORTANTE: Riproduci prerolls SOLO se siamo all'inizio (currentTime ~= 0)
      const { adsPlayed } = this.state.getState();
      const isAtStart = !this.audioElement.currentTime || this.audioElement.currentTime < 1;
      
      if (isAtStart && this.adManager) {
        const prerolls = this.adManager.getPrerolls();
        const unplayedPrerolls = prerolls.filter(ad => !adsPlayed.includes(ad.id));
        
        if (unplayedPrerolls.length > 0) {
          console.log('▶️ Riproduzione prerolls prima del contenuto:', unplayedPrerolls.length);
          this.state.setState({ isAdPlaying: true, currentAdType: 'preroll' });
          if (this.controlsManager) this.controlsManager.disableControlsForAd();
          
          await this.adManager.playPrerolls(this.getHostElement());
          
          this.state.setState({ isAdPlaying: false, currentAdType: null, currentAdId: null });
          if (this.controlsManager) this.controlsManager.enableControlsAfterAd();
          console.log('✅ Prerolls completati - inizio contenuto principale');
        }
      }
      
      // Riproduci contenuto principale
      await this.playMainAudio();
      this.state.setState({ isPlaying: true });
      
    } catch (e) {
      console.error('Errore riproduzione:', e);
      throw e;
    }
  }
  
  /**
   * Play audio principale
   */
  async playMainAudio() {
    if (!this.audioElement) return;
    
    try {
      // Verifica che l'audio sia caricato
      if (!this.audioElement.src) {
        await this.loadAudio();
      }
      
      // Verifica stato audio prima di play
      console.log('▶️ Tentativo play audio:', {
        src: this.audioElement.src,
        readyState: this.audioElement.readyState,
        networkState: this.audioElement.networkState,
        paused: this.audioElement.paused,
        error: this.audioElement.error
      });
      
      await this.audioElement.play();
      
      console.log('✅ Audio in riproduzione');
    } catch (e) {
      console.error('Errore riproduzione audio principale:', e);
      console.error('Audio element state:', {
        src: this.audioElement.src,
        readyState: this.audioElement.readyState,
        networkState: this.audioElement.networkState,
        error: this.audioElement.error
      });
      throw e;
    }
  }
  
  /**
   * Pause audio
   */
  pause() {
    if (this.audioElement) {
      this.audioElement.pause();
    }
    
    this.state.setState({ isPlaying: false });
  }
  
  /**
   * Skip secondi
   * @param {number} seconds - Secondi da skippare
   */
  skip(seconds) {
    const { isAdPlaying } = this.state.getState();
    if (isAdPlaying || !this.audioElement) return; // Non skippare durante ads
    
    const newTime = this.audioElement.currentTime + seconds;
    const duration = this.state.getState().duration;
    this.audioElement.currentTime = Math.max(0, Math.min(newTime, duration));
  }
  
  /**
   * Seek a posizione
   * @param {number} percentage - Percentuale (0-1)
   */
  seek(percentage) {
    const { isAdPlaying, duration } = this.state.getState();
    if (isAdPlaying || !this.audioElement) return; // Non seekare durante ads
    
    this.audioElement.currentTime = percentage * duration;
  }
  
  /**
   * Imposta volume
   * 
   * Usa GainNode per compatibilità iOS (dove audioElement.volume è bloccato).
   * 
   * @param {number} value - Valore volume (0.0 - 1.0)
   * @since v2.4.1 - Usa GainNode per main audio, compatibile iOS
   * @since v3.0.0 - Rimosso supporto preroll volume (gestito da AdManager)
   */
  setVolume(value) {
    const { isMuted } = this.state.getState();
    
    // Main audio: usa GainNode (iOS-compatible)
    if (this.gainNode) {
      this.gainNode.gain.value = value;
      console.log('🎚️ Volume impostato via GainNode:', value);
    } else if (this.audioElement) {
      // Fallback per pre-Web Audio API setup
      this.audioElement.volume = value;
      console.log('🎚️ Volume impostato via audioElement:', value);
    } else {
      console.warn('⚠️ setVolume chiamato ma né GainNode né audioElement disponibili');
    }
    
    // Applica volume anche agli ads se AdManager presente
    if (this.adManager && this.adManager.adAudioElement) {
      this.adManager.adAudioElement.volume = value;
    }
    
    const updates = { volume: value };
    if (!isMuted && value > 0) {
      updates.previousVolume = value;
    }
    
    this.state.setState(updates);
  }
  
  /**
   * Toggle mute
   */
  toggleMute() {
    const { isMuted, volume, previousVolume } = this.state.getState();
    
    if (isMuted) {
      this.setVolume(previousVolume);
      this.state.setState({ isMuted: false });
    } else {
      this.state.setState({ previousVolume: volume });
      this.setVolume(0);
      this.state.setState({ isMuted: true });
    }
  }
  
  /**
   * Setup Web Audio API per waveform e controllo volume iOS-compatible
   * 
   * Catena audio: source → gainNode → analyser → destination
   * Il GainNode permette il controllo volume anche su iOS, dove audioElement.volume è bloccato
   * 
   * @param {number} fftSize - FFT size
   * @returns {Object} { analyser, dataArray }
   * @since v2.4.1 - Aggiunto GainNode per supporto volume iOS
   */
  setupWebAudio(fftSize = 256) {
    if (!this.audioContext) {
      console.log('🔧 Inizializzazione Web Audio API...');
      
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('📱 AudioContext creato. Stato:', this.audioContext.state);
      
      const source = this.audioContext.createMediaElementSource(this.audioElement);
      
      // GainNode per controllo volume (iOS-compatible)
      this.gainNode = this.audioContext.createGain();
      this.gainNode.gain.value = this.state.getState().volume;
      console.log('🎚️ GainNode creato. Volume iniziale:', this.gainNode.gain.value);
      
      // Analyser per waveform
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = fftSize;
      
      // Catena: source → gain → analyser → destination
      source.connect(this.gainNode);
      this.gainNode.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);
      
      this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);
      
      console.log('✅ Catena audio: AudioElement → GainNode → AnalyserNode → Speakers');
      console.log('🎚️ GainNode pronto per controllo volume iOS-compatible');
    }
    
    return {
      analyser: this.analyser,
      dataArray: this.dataArray
    };
  }
  
  /**
   * Resume audio context
   */
  async resumeAudioContext() {
    if (this.audioContext) {
      const stateBefore = this.audioContext.state;
      
      if (stateBefore === 'suspended') {
        console.log('🔄 AudioContext suspended, riprendo...');
        await this.audioContext.resume();
        console.log('✅ AudioContext resumed. Nuovo stato:', this.audioContext.state);
      } else {
        console.log('ℹ️ AudioContext già attivo. Stato:', stateBefore);
      }
    } else {
      console.warn('⚠️ resumeAudioContext chiamato ma audioContext non esiste');
    }
  }
  
  /**
   * Cleanup
   */
  destroy() {
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.src = '';
    }
    
    if (this.audioContext) {
      this.audioContext.close();
    }
    
    if (this.adManager) {
      this.adManager.destroy();
    }
  }
}

